/**
 * "Station health" (help `cloud.station-health`; docs/specs/cloud/station-health.md; Diurnal parity C2: 02-003,
 * 02-005). The organisation's TA Live base stations as Teslyn Cloud checks them: each unit's state, newest reading
 * and sensor clock, its sensor day's clock drift, time zone and latency jitter, and its "Offline alerts" switch; and
 * the TA Live connection with "Reset connection" for tenant admins and managers. Staff only: no client role has the
 * page, and the database gives clients nothing. The rules and words are the domain's (`station-health.ts`).
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  canResetStationConnector,
  canSetStationAlerts,
  clockAdvice,
  connectorText,
  diagnosticsLines,
  stationClockLabel,
  stationDot,
  stationRowClock,
  type StationDot,
  type StationHealthAnswer,
  type StationHealthRow,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadStationHealth, resetConnector, setOfflineAlerts, setOnlineMinutes } from './api.ts';
import { StationProjectsPanel } from './StationProjectsPanel.tsx';
import { TableWrap } from '../components/TableWrap.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const DOT: Readonly<Record<StationDot, { word: string; chip: string }>> = {
  online: { word: 'Online', chip: 'chip chip--pass' },
  offline: { word: 'Offline', chip: 'chip chip--fail' },
  stale: { word: 'Not checked lately', chip: 'chip chip--warn' },
  unknown: { word: 'Not checked yet', chip: 'chip' },
};

/** "18 Oct 10:42" in a zone `offsetH` hours east of UTC. */
function at(iso: string | null, offsetH: number): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms + Math.round(offsetH * 3_600_000));
  return `${d.getUTCDate()} ${d.toLocaleString('en-AU', { month: 'short', timeZone: 'UTC' })} ${d.toISOString().slice(11, 16)}`;
}

const RESETTABLE = new Set(['paused', 'failing', 'refused', 'rate_limited']);

export interface StationHealthScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly roles: readonly string[];
  /** The clock (tests). */
  readonly now?: () => number;
  /** The zone times are shown in, hours east of UTC (default: this browser's). */
  readonly displayOffsetH?: number;
}

export function StationHealthScreen({
  sb,
  tenantId,
  roles,
  now = () => Date.now(),
  displayOffsetH = -new Date().getTimezoneOffset() / 60,
}: StationHealthScreenProps): ReactElement {
  const [answer, setAnswer] = useState<StationHealthAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const mayReset = canResetStationConnector(roles);
  const maySetOnline = roles.includes('tenant_admin');
  const [minutes, setMinutes] = useState('');
  const maySetAlerts = canSetStationAlerts(roles);
  const zone = stationClockLabel(displayOffsetH);

  const reload = useCallback(() => {
    setError(null);
    loadStationHealth(sb, tenantId)
      .then((a) => {
        setAnswer(a);
        setMinutes(String(a.online_minutes));
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  const nowMs = now();

  async function reset(): Promise<void> {
    setBusy('reset');
    setNotice(null);
    try {
      const done = await resetConnector(sb, tenantId);
      setNotice({
        ok: done,
        text: done
          ? 'Connection reset: Teslyn Cloud checks the stations within 5 minutes.'
          : 'There is no TA Live connection to reset yet.',
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  async function saveMinutes(): Promise<void> {
    const n = Number(minutes);
    if (!Number.isInteger(n) || n < 1 || n > 240) {
      setNotice({ ok: false, text: 'Enter 1 to 240 minutes.' });
      return;
    }
    setBusy('minutes');
    setNotice(null);
    try {
      await setOnlineMinutes(sb, tenantId, n);
      setNotice({
        ok: true,
        text: `A unit now counts as online while its newest reading is at most ${n} minutes old.`,
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  async function toggleAlerts(s: StationHealthRow): Promise<void> {
    setBusy(s.id);
    setNotice(null);
    try {
      await setOfflineAlerts(sb, s.id, !s.offline_alerts);
      setNotice({
        ok: true,
        text: s.offline_alerts
          ? `${s.name}: offline alerts off.`
          : `${s.name}: offline alerts on. Staff who receive base station alerts are told when it sends nothing for ${answer?.online_minutes ?? 15} minutes.`,
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="page page--wide">
      <PageHeader
        title="Station health"
        subtitle="Your TA Live base stations as Teslyn Cloud checks them: every 15 minutes by day and every hour at night. Teslyn Field shows the same on its Diurnal QC screen."
      >
        <Button variant="quiet" onClick={reload}>
          Refresh
        </Button>
      </PageHeader>
      {error && <ErrorState message={error} onRetry={reload} />}
      {!answer && !error && <Loading what="station health" />}
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {answer && (
        <>
          <div className="panel" aria-label="TA Live connection">
            <h2 className="heading">TA Live connection</h2>
            <p className="body">{connectorText(answer.connector, nowMs, displayOffsetH)}</p>
            {answer.connector?.message && <p className="caption muted">{answer.connector.message}</p>}
            <p className="caption muted">
              A unit counts as online while its newest reading is at most {answer.online_minutes} minutes old;
              the offline alert follows the same rule.
            </p>
            {maySetOnline && (
              <div className="row">
                <label className="row">
                  <span className="body">Online within (minutes)</span>
                  <input
                    className="input data"
                    inputMode="numeric"
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    aria-label="Online within (minutes)"
                  />
                </label>
                <Button
                  busy={busy === 'minutes'}
                  disabled={busy !== null || minutes === String(answer.online_minutes)}
                  onClick={() => void saveMinutes()}
                >
                  Save
                </Button>
              </div>
            )}
            {mayReset && answer.connector && RESETTABLE.has(answer.connector.state) && (
              <Button
                variant="primary"
                busy={busy === 'reset'}
                disabled={busy !== null}
                onClick={() => void reset()}
              >
                Reset connection
              </Button>
            )}
          </div>
          {answer.stations.length === 0 ? (
            <div className="panel">
              <p className="body">
                No TA Live stations are registered for your organisation yet. Braynewave adds them when TA
                Live is set up.
              </p>
            </div>
          ) : (
            <TableWrap>
              <table className="table" aria-label="Stations">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Status</th>
                    <th>Newest reading ({zone})</th>
                    <th className="num">nT</th>
                    <th>Sensor clock</th>
                    <th>Clock drift</th>
                    <th>Time zone</th>
                    <th>Latency jitter</th>
                    <th>Offline alerts</th>
                  </tr>
                </thead>
                <tbody>
                  {answer.stations.map((s) => {
                    const h = s.health;
                    const dot = stationDot(s, nowMs);
                    const d = h?.diagnostics ?? null;
                    const lines = d ? diagnosticsLines(d) : null;
                    return (
                      <tr key={s.id} aria-label={s.name}>
                        <td>
                          {s.name}
                          {s.station_number && (
                            <span className="table__line muted">Station {s.station_number}</span>
                          )}
                        </td>
                        <td>
                          <span className={DOT[dot].chip}>{DOT[dot].word}</span>
                          {h?.status === 'no_recent' && dot !== 'stale' && (
                            <span className="table__line muted">No readings in the last days</span>
                          )}
                          {h && (
                            <span className="table__line muted">
                              Checked {at(h.checked_at, displayOffsetH)}
                            </span>
                          )}
                          {h?.message && <span className="table__line muted">{h.message}</span>}
                        </td>
                        <td>{at(h?.last_reading_at ?? null, displayOffsetH)}</td>
                        <td className="num">
                          {h?.last_value_nt === null || !h ? '—' : h.last_value_nt.toFixed(1)}
                        </td>
                        <td>{stationRowClock(s)}</td>
                        <td>
                          {lines ? lines.drift : '—'}
                          {d && <span className="table__line muted">{clockAdvice(d)}</span>}
                          {h?.diagnostics_day && (
                            <span className="table__line muted">Sensor day {h.diagnostics_day}</span>
                          )}
                        </td>
                        <td>{lines ? lines.timeZone : '—'}</td>
                        <td>{lines ? lines.latency : '—'}</td>
                        <td>
                          {maySetAlerts ? (
                            <label className="row">
                              <input
                                type="checkbox"
                                checked={s.offline_alerts}
                                disabled={busy !== null}
                                onChange={() => void toggleAlerts(s)}
                                aria-label={`Offline alerts for ${s.name}`}
                              />
                              <span className="body">{s.offline_alerts ? 'On' : 'Off'}</span>
                            </label>
                          ) : s.offline_alerts ? (
                            'On'
                          ) : (
                            'Off'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrap>
          )}
          <StationProjectsPanel
            sb={sb}
            tenantId={tenantId}
            roles={roles}
            units={answer.stations.map((s) => ({ id: s.id, name: s.name }))}
            today={new Date(nowMs + Math.round(displayOffsetH * 3_600_000)).toISOString().slice(0, 10)}
          />
        </>
      )}
    </section>
  );
}
